import { useCallback, useEffect, useRef, useState } from 'react';
import type { DashboardDoc, DashboardTile, DashboardTileLayout, DashboardVisual } from './dashboardTypes';
import { migrateTileLayout } from './widgets/widgetLayout';

const DB_NAME = 'aleno-maitre-dashboard';
const STORE = 'dashboards';
const DB_VERSION = 1;

function emptyDoc(scopeKey: string): DashboardDoc {
  return {
    scopeKey,
    tiles: [],
    updatedAt: new Date().toISOString(),
  };
}

function normalizeDoc(scopeKey: string, raw: DashboardDoc | undefined): DashboardDoc {
  if (!raw) return emptyDoc(scopeKey);
  return {
    scopeKey: raw.scopeKey || scopeKey,
    tiles: migrateTileLayout(raw.tiles ?? []),
    updatedAt: raw.updatedAt || new Date().toISOString(),
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'scopeKey' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

async function loadDoc(scopeKey: string): Promise<DashboardDoc> {
  const db = await openDb();
  try {
    const doc = await new Promise<DashboardDoc | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(scopeKey);
      req.onsuccess = () => resolve(req.result as DashboardDoc | undefined);
      req.onerror = () => reject(req.error);
    });
    return normalizeDoc(scopeKey, doc);
  } finally {
    db.close();
  }
}

async function saveDoc(doc: DashboardDoc): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(doc);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

export function useDashboardStore(scopeKey: string) {
  const [doc, setDoc] = useState<DashboardDoc>(() => emptyDoc(scopeKey));
  const [ready, setReady] = useState(false);
  const docRef = useRef(doc);
  const savingRef = useRef(false);
  const saveQueuedRef = useRef(false);
  docRef.current = doc;

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    void loadDoc(scopeKey)
      .then(next => {
        if (cancelled) return;
        docRef.current = next;
        setDoc(next);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        const empty = emptyDoc(scopeKey);
        docRef.current = empty;
        setDoc(empty);
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeKey]);

  const persist = useCallback(async (next: DashboardDoc) => {
    docRef.current = next;
    setDoc(next);
    saveQueuedRef.current = true;
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      while (saveQueuedRef.current) {
        saveQueuedRef.current = false;
        const snapshot = docRef.current;
        try {
          await saveDoc(snapshot);
        } catch {
          /* quota — keep memory copy */
        }
      }
    } finally {
      savingRef.current = false;
    }
  }, []);

  const patchDoc = useCallback(
    async (recipe: (prev: DashboardDoc) => DashboardDoc) => {
      await persist(recipe(docRef.current));
    },
    [persist],
  );

  const pinTile = useCallback(
    async (tile: DashboardTile) => {
      await patchDoc(prev => {
        const without = prev.tiles.filter(item => item.id !== tile.id);
        return {
          ...prev,
          scopeKey,
          tiles: [...without, tile],
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [patchDoc, scopeKey],
  );

  const unpinTile = useCallback(
    async (tileId: string) => {
      await patchDoc(prev => ({
        ...prev,
        scopeKey,
        tiles: prev.tiles.filter(tile => tile.id !== tileId),
        updatedAt: new Date().toISOString(),
      }));
    },
    [patchDoc, scopeKey],
  );

  const reorderTiles = useCallback(
    async (fromId: string, toId: string) => {
      if (fromId === toId) return;
      await patchDoc(prev => {
        const tiles = [...prev.tiles];
        const from = tiles.findIndex(tile => tile.id === fromId);
        const to = tiles.findIndex(tile => tile.id === toId);
        if (from < 0 || to < 0) return prev;
        const [moved] = tiles.splice(from, 1);
        tiles.splice(to, 0, moved);
        return {
          ...prev,
          scopeKey,
          tiles,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [patchDoc, scopeKey],
  );

  const replaceTiles = useCallback(
    async (tiles: DashboardTile[]) => {
      await patchDoc(prev => ({
        ...prev,
        scopeKey,
        tiles,
        updatedAt: new Date().toISOString(),
      }));
    },
    [patchDoc, scopeKey],
  );

  const applyVisuals = useCallback(
    async (updates: DashboardVisual[]) => {
      if (updates.length === 0) return;
      const byId = new Map(updates.map(visual => [visual.id, visual]));
      await patchDoc(prev => ({
        ...prev,
        scopeKey,
        tiles: prev.tiles.map(tile => ({
          ...tile,
          visuals: tile.visuals.map(visual => byId.get(visual.id) ?? visual),
        })),
        updatedAt: new Date().toISOString(),
      }));
    },
    [patchDoc, scopeKey],
  );

  const updateTileLayouts = useCallback(
    async (updates: Array<{ id: string; layout: DashboardTileLayout }>) => {
      if (updates.length === 0) return;
      const byId = new Map(updates.map(item => [item.id, item.layout]));
      await patchDoc(prev => ({
        ...prev,
        scopeKey,
        tiles: prev.tiles.map(tile => {
          const layout = byId.get(tile.id);
          return layout ? { ...tile, layout } : tile;
        }),
        updatedAt: new Date().toISOString(),
      }));
    },
    [patchDoc, scopeKey],
  );

  const updateTileLayout = useCallback(
    async (tileId: string, layout: DashboardTileLayout) => {
      await updateTileLayouts([{ id: tileId, layout }]);
    },
    [updateTileLayouts],
  );

  const isPinned = useCallback(
    (id: string) => doc.tiles.some(tile => tile.id === id),
    [doc.tiles],
  );

  return {
    tiles: doc.tiles,
    ready,
    pinTile,
    unpinTile,
    reorderTiles,
    replaceTiles,
    applyVisuals,
    updateTileLayout,
    updateTileLayouts,
    isPinned,
  };
}
